import React, { useMemo, useState } from "react"
import {
  Box, Flex, Text,
} from "@chakra-ui/react"
import { useQueryClient } from "react-query"
import { useHistory, useParams } from "react-router-dom"
import Icon from "components/elements/Icon"
import { Combobox } from "components/Combobox"
import { Guest, Seat, Table } from "sharedTypes"
import * as guestsApi from "api/guests"
import { updateGuest, updateTables } from "context/actions"
import { guestSummary } from "utilities/objects"
import { eventSeatingPath } from "utilities/routes"
import DropdownToggle from "../DropdownToggle"

export const updateTablesFromGuest = (guest: Guest, tables: Table[]) => {
  const {
    seatingAssignment: nextSeatingAssignment,
    waitlistTable: nextWaitlistTable,
  } = guest

  return tables.map((table) => {
    const waitlistedGuests = table.waitlistedGuests.filter(({ id }) => id !== guest.id)
    const waitlistedGuestIds = table.waitlistedGuestIds.filter((id) => id !== guest.id)

    if (nextWaitlistTable?.id === table.id) {
      waitlistedGuests.push(guestSummary(guest))
      waitlistedGuestIds.push(guest.id)
    }

    const seats = table.seats.map((seat) => {
      if (seat.guest?.id && seat.guest?.id !== guest.id) return seat
      if (
        table.id === nextSeatingAssignment?.seatingTableId
        && seat.number === nextSeatingAssignment.seat
      ) {
        return { ...seat, guestId: guest.id, guest: guestSummary(guest) }
      }

      return { ...seat, guestId: null, guest: null }
    })

    return {
      ...table, seats, waitlistedGuests, waitlistedGuestIds,
    }
  })
}

// Heterogeneous items because step 1 (tables + No Seat + Standing) and
// step 2 (seats + Waitlist) share the same Combobox.List but render
// differently per kind.
type NoSeatItem = { kind: "noSeat" }
type StandingItem = { kind: "standing" }
type TableItem = { kind: "table"; table: Table }
type SeatItem = {
  kind: "seat"
  number: number
  takenByGuestId: number | string | null
  takenByGuestName: string | null
}
type WaitlistItem = { kind: "waitlist" }
type SeatingItem = NoSeatItem | StandingItem | TableItem | SeatItem | WaitlistItem

const getItemKey = (item: SeatingItem): string => {
  switch (item.kind) {
    case "noSeat": return "no-seat"
    case "standing": return "standing"
    case "table": return `table-${item.table.id}`
    case "seat": return `seat-${item.number}`
    case "waitlist": return "waitlist"
    default: return ""
  }
}

const getItemLabel = (item: SeatingItem): string => {
  switch (item.kind) {
    case "noSeat": return "No Seat"
    case "standing": return "Standing"
    case "table": return item.table.name
    case "seat": return `Seat ${item.number}`
    case "waitlist": return "Waitlist"
    default: return ""
  }
}

type Props = { guest: Guest, tables: Table[], disabled: boolean }

const Seating = ({ guest, tables, disabled }: Props) => {
  const queryClient = useQueryClient()
  const history = useHistory()
  const { eventId } = useParams<{ eventId: string }>()
  const { seatingAssignment: assignment, waitlistTable } = guest
  const [selectedTable, setSelectedTable] = useState<Table | null>(null)

  const updateStateAndReset = (data: Guest) => {
    updateGuest(queryClient, data)
    updateTables(queryClient, eventId, updateTablesFromGuest(data, tables))
    setSelectedTable(null)
  }

  const removeSeat = () => {
    guestsApi.deleteGuestSeat(eventId, guest.id).then(({ data }) => updateStateAndReset(data))
  }
  const setStanding = () => {
    guestsApi.putGuest(eventId, guest.id, { standing: true })
      .then(({ data }) => updateStateAndReset(data))
  }
  const setSeat = (number: number) => {
    if (!selectedTable) return
    guestsApi.postGuestSeat(eventId, guestSummary(guest), selectedTable, number)
      .then(({ data }) => updateStateAndReset(data))
  }
  const setWaitlist = () => {
    if (!selectedTable) return
    guestsApi.postGuestTableWaitlist(eventId, guest.id, selectedTable)
      .then(({ data }) => updateStateAndReset(data))
  }

  const items: SeatingItem[] = useMemo(() => {
    if (selectedTable) {
      return [
        ...selectedTable.seats.map((s: Seat): SeatItem => ({
          kind: "seat",
          number: s.number,
          takenByGuestId: s.guest?.id ?? null,
          takenByGuestName: s.guest?.name ?? null,
        })),
        { kind: "waitlist" } as WaitlistItem,
      ]
    }

    return [
      { kind: "noSeat" } as NoSeatItem,
      { kind: "standing" } as StandingItem,
      ...tables.map((t): TableItem => ({ kind: "table", table: t })),
    ]
  }, [selectedTable, tables])

  // What's currently selected as "the answer" — drives the blue-tint +
  // checkmark indicator on the matching row.
  const currentSelection: SeatingItem | null = useMemo(() => {
    if (selectedTable) {
      if (assignment && assignment.seatingTableId === selectedTable.id) {
        return {
          kind: "seat", number: assignment.seat, takenByGuestId: null, takenByGuestName: null,
        }
      }
      if (waitlistTable && waitlistTable.id === selectedTable.id) return { kind: "waitlist" }

      return null
    }
    if (guest.standing) return { kind: "standing" }
    if (assignment) {
      const t = tables.find((x) => x.id === assignment.seatingTableId)

      return t ? { kind: "table", table: t } : null
    }
    if (waitlistTable) {
      const t = tables.find((x) => x.id === waitlistTable.id)

      return t ? { kind: "table", table: t } : null
    }

    return { kind: "noSeat" }
  }, [selectedTable, assignment, waitlistTable, guest.standing, tables])

  const title = (() => {
    if (assignment) return `${assignment.seatingTableTitle}: Seat ${assignment.seat}`
    if (waitlistTable) return `${waitlistTable.name}: Waitlisted`
    if (guest.standing) return "Standing"

    return "No Seat"
  })()

  // Reset to step 1 on open. Doing the reset on open (not on close) avoids
  // a visible flicker during the close animation — any leftover step-2 state
  // gets overwritten at the moment the popover re-opens, still off-screen.
  const handleOpenChange = (open: boolean) => {
    if (open) setSelectedTable(null)
  }

  // Route everything through onChange so mouse-click and keyboard-Enter
  // behave the same way. Return `false` to keep the popover open: tables
  // navigate to step 2, and taken seats are a no-op.
  const handleChange = (item: SeatingItem | null): boolean | void => {
    if (!item) return undefined
    switch (item.kind) {
      case "table":
        setSelectedTable(item.table)

        return false
      case "seat": {
        const takenByOther = item.takenByGuestId !== null
          && String(item.takenByGuestId) !== String(guest.id)
        if (takenByOther) return false
        setSeat(item.number)
        break
      }
      case "noSeat": removeSeat(); break
      case "standing": setStanding(); break
      case "waitlist": setWaitlist(); break
      default: break
    }

    return undefined
  }

  return (
    <Combobox.Root<SeatingItem>
      items={items}
      getItemKey={getItemKey}
      getItemLabel={getItemLabel}
      value={currentSelection}
      onChange={handleChange}
      onOpenChange={handleOpenChange}
      // Wider indicator slot so size={8} table-shape icons fit in the same
      // column the checkmark / leading-icon would occupy on other rows.
      slotWidth={8}
    >
      <Combobox.Trigger>
        <Box
          pointerEvents={disabled ? "none" : "auto"}
          opacity={disabled ? 0.6 : 1}
        >
          <DropdownToggle
            icon={<Icon icon="invite-seat" size={6} mr={2} />}
            title={title}
            disabled={disabled}
          />
        </Box>
      </Combobox.Trigger>
      <Combobox.Content w="320px">
        <Combobox.Header>
          {selectedTable ? (
            <Flex align="center" justify="space-between" w="full">
              <Flex
                as="button"
                align="center"
                fontSize="sm"
                color="blue.500"
                onClick={() => setSelectedTable(null)}
              >
                <Icon icon="prev-arrow" mr={2} /> Tables
              </Flex>
              <Box flex={1} textAlign="center" fontSize="md">
                {selectedTable.name}
              </Box>
              {/* invisible spacer matching the back button's footprint
                  so the table name centers properly */}
              <Box w="80px" />
            </Flex>
          ) : (
            "Seating"
          )}
        </Combobox.Header>
        {!selectedTable && (
          <Combobox.Input placeholder="Search Table Names" />
        )}
        <Combobox.List<SeatingItem> maxH="400px">
          {(item, index) => {
            // Common BoxProps so every row matches the legacy DropdownItem
            // style (big padding, lg font, top-border as a visual separator).
            // The first row's top border is suppressed because the Input
            // (or Header, when no Input) already provides a bottom border —
            // otherwise we'd render a visible 2px line.
            const rowStyle = {
              p: 5,
              fontSize: "lg",
              borderTopWidth: index === 0 ? 0 : 1,
              borderColor: "gray.200",
            } as const

            if (item.kind === "table") {
              const assignedSeats = item.table.seats.filter((s) => s.guest).length
              const vacantSeats = item.table.seats.length - assignedSeats

              return (
                // Tables already have a leading shape icon — suppress the
                // checkmark to avoid stacking two icons. The blue background
                // tint on the selected row is signal enough.
                <Combobox.Item item={item} hideCheckmark {...rowStyle}>
                  <Flex align="center" my={-2} overflow="hidden" flexGrow={1}>
                    {/* size={8} matches Root's slotWidth={8} so the table
                        icon occupies the same column the checkmark would
                        occupy in other rows — keeps text aligned across
                        icon-bearing and non-icon rows. */}
                    <Icon icon={`table-${item.table.shape}`} size={8} mr={2} />
                    <Box lineHeight={6} flexGrow={1}>
                      <Text fontSize="lg" w="full" noOfLines={1} title={item.table.name}>
                        {item.table.name}
                      </Text>
                      <Text fontSize="sm">
                        {assignedSeats} Assigned{" "}
                        <Text as="span" color={vacantSeats ? "green.500" : "red.500"}>
                          ({vacantSeats} Vacant)
                        </Text>
                      </Text>
                    </Box>
                    <Icon icon="next-arrow" />
                  </Flex>
                </Combobox.Item>
              )
            }

            if (item.kind === "seat") {
              const takenByOther = item.takenByGuestId !== null
                && String(item.takenByGuestId) !== String(guest.id)
              if (takenByOther) {
                return (
                  <Combobox.Item
                    item={item}
                    cursor="not-allowed"
                    {...rowStyle}
                    color="gray.300"
                  >
                    {`Seat ${item.number} (${item.takenByGuestName})`}
                  </Combobox.Item>
                )
              }

              return (
                <Combobox.Item item={item} {...rowStyle}>
                  {`Seat ${item.number} (Vacant)`}
                </Combobox.Item>
              )
            }

            return (
              <Combobox.Item item={item} {...rowStyle}>
                {getItemLabel(item)}
              </Combobox.Item>
            )
          }}
        </Combobox.List>
        {/* Step 1 empty state — "No results" plus "Edit Seating" as a
            keyboard-reachable escape hatch to the settings page. Step 2
            ("inside a table") never renders empty since seats are always
            present, so leaving this outside the selectedTable guard is fine. */}
        {!selectedTable && (
          <Combobox.Empty>
            {(query) => (
              <>
                <Text textAlign="center" color="gray.500" py={4}>
                  {query ? `No tables match "${query}"` : "No tables yet"}
                </Text>
                <Combobox.Action
                  onSelect={() => history.push(eventSeatingPath(eventId))}
                  color="blue.400"
                  borderTopWidth={1}
                  borderColor="gray.200"
                >
                  <Icon icon="pencil" size={5} mr={2} color="blue.400" />
                  <Text>Edit Seating</Text>
                </Combobox.Action>
              </>
            )}
          </Combobox.Empty>
        )}
      </Combobox.Content>
    </Combobox.Root>
  )
}

export default Seating
